import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Panel, Loader } from "@/components/portal/Shared";
import { EvidenceBadge } from "@/components/portal/EvidenceBadge";
import { toast } from "sonner";
import { ScanText, Loader2, Sparkles } from "lucide-react";

const FieldList = ({ label, items }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="mb-4">
      <div className="text-xs uppercase font-mono tracking-wider text-[#8F7220] mb-1.5">{label}</div>
      <div className="flex flex-wrap gap-2">
        {items.map((it, i) => (
          <span key={i} className="text-sm bg-[#F3EEFA] text-[#3F1E68] rounded-lg px-2.5 py-1">
            {typeof it === "string" ? it : it.value ? `${it.value}${it.event ? " — " + it.event : ""}` : it.name ? `${it.name}${it.relationship ? " (" + it.relationship + ")" : ""}` : JSON.stringify(it)}
          </span>
        ))}
      </div>
    </div>
  );
};

export default function RecordReader() {
  const [docs, setDocs] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/documents").then((r) => {
      const withText = r.data.filter((d) => d.extracted_text);
      setDocs(withText);
      if (withText[0]) setSelectedId(withText[0].id);
    });
  }, []);

  const run = async () => {
    if (!selectedId) return;
    setLoading(true); setResult(null);
    try {
      const r = await api.post("/record-reader/extract", { document_id: selectedId });
      setResult(r.data);
    } catch (e) { toast.error("Extraction failed"); }
    finally { setLoading(false); }
  };

  const selectedDoc = docs.find((d) => d.id === selectedId);

  return (
    <div data-testid="module-record-reader">
      <PageHeader
        eyebrow="Record Analyst AI • Claude Sonnet 4.6"
        title="AI Record Reader"
        subtitle="Extract names, dates, places, tribal names, rolls, agencies and citations from a document. The AI reads only the record and reports evidence — it never declares ancestry."
      />

      <div className="grid lg:grid-cols-2 gap-6">
        <Panel className="p-6">
          <label className="text-xs uppercase font-mono tracking-wider text-[#8F7220]">Source Document</label>
          <select
            data-testid="reader-doc-select"
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            className="mt-2 w-full rounded-xl border border-[#E4DCD3] bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50"
          >
            {docs.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
          </select>

          {selectedDoc && (
            <div className="mt-4 rounded-xl bg-[#FAF8F5] border border-[#E4DCD3] p-4 max-h-64 overflow-y-auto hp-scroll">
              <div className="text-xs uppercase font-mono tracking-wider text-[#8F7220] mb-1.5">Document Text (OCR)</div>
              <p className="text-sm text-[#1A171D] leading-relaxed">{selectedDoc.extracted_text}</p>
            </div>
          )}

          <button
            data-testid="reader-extract-btn"
            onClick={run}
            disabled={loading || !selectedId}
            className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#2A1442] text-white px-5 py-3 text-sm font-semibold hover:bg-[#482276] transition-colors disabled:opacity-60"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <ScanText size={16} />}
            {loading ? "Reading record…" : "Extract Structured Data"}
          </button>
        </Panel>

        <Panel className="p-6" data-testid="reader-result">
          {!result && !loading && (
            <div className="h-full grid place-items-center text-center text-[#756D7E] py-16">
              <div>
                <Sparkles size={28} className="text-[#D4AF37] mx-auto mb-3" />
                <p className="text-sm">Extracted entities will appear here.</p>
              </div>
            </div>
          )}
          {loading && <Loader label="Claude is reading the record…" />}
          {result && (
            <div className="hp-fade-up">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-serif text-lg font-semibold text-[#180D24]">Extracted Entities</h3>
                <div className="text-right">
                  <div className="text-xs text-[#756D7E]">Confidence</div>
                  <div className="font-mono font-bold text-[#59288E]">{result.extraction.confidence ?? "—"}%</div>
                </div>
              </div>
              <div className="mb-4"><EvidenceBadge status={result.extraction.suggested_evidence_status} size="lg" /></div>
              {result.extraction.summary && <p className="text-sm text-[#1A171D] mb-4 italic">{result.extraction.summary}</p>}
              <FieldList label="Names" items={result.extraction.names} />
              <FieldList label="Dates" items={result.extraction.dates} />
              <FieldList label="Places" items={result.extraction.places} />
              <FieldList label="Relatives" items={result.extraction.relatives} />
              <FieldList label="Tribal Names" items={result.extraction.tribal_names} />
              <FieldList label="Racial Classifications (as recorded)" items={result.extraction.racial_classifications} />
              <FieldList label="Rolls" items={result.extraction.rolls} />
              <FieldList label="Agencies" items={result.extraction.agencies} />
              <FieldList label="Land Records" items={result.extraction.land_records} />
              <FieldList label="Citations" items={result.extraction.citations} />
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}
